import React, { useRef, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { Layer, Shape } from 'react-konva';
import * as d3 from 'd3';
import 'd3-contour';

const ContourMapLayer = ({ gridValues, gridRect, scale, contourValues, autoContourValues, contourColors }) => {
    const shapeRef = useRef();
    const throttleRef = useRef(false); // throttle flag
  
    // Compute grid dimensions based on gridRect.
    const numCols = useMemo(
      () => Math.round(gridRect.origin.x.length / gridRect.xSpacing),
      [gridRect]
    );
    const numRows = useMemo(
      () => Math.round(gridRect.origin.y.length / gridRect.ySpacing),
      [gridRect]
    );
  
    // Derived values from gridValues.
    const dataMax = useMemo(() => Math.max(...gridValues), [gridValues]);
    const dataMin = 1;
  
    // Create 7 evenly spaced thresholds from dataMin to dataMax.
    const thresholds = useMemo(() => {
      if (!autoContourValues) {
        return contourValues;
      } else {
        return d3.range(7).map(i => dataMin + (i / 7) * (dataMax - dataMin));
      }
    }, [autoContourValues, contourValues, dataMin, dataMax]);
  
    // Create a color scale using d3.interpolateSpectral.
    const colorScale = useMemo(
      () => d3.scaleSequential(d3.interpolateSpectral).domain([dataMax, dataMin]),
      [dataMax, dataMin]
    );
  
    // Generate contours using d3-contour.
    const contours = useMemo(() => {
      if (!gridValues.length) return [];
      const contourGenerator = d3
        .contours()
        .size([numCols, numRows])
        .thresholds(thresholds);
      return contourGenerator(gridValues);
    }, [gridValues, numCols, numRows, thresholds]);
  
    // Scaling functions: map grid cell coordinates to pixel coordinates.
    const xScale = useMemo(
      () =>
        d3
          .scaleLinear()
          .domain([0, numCols])
          .range([
            gridRect.origin.x.start,
            gridRect.origin.x.start + gridRect.origin.x.length,
          ]),
      [gridRect, numCols]
    );
    const yScale = useMemo(
      () =>
        d3
          .scaleLinear()
          .domain([0, numRows])
          .range([
            gridRect.origin.y.start,
            gridRect.origin.y.start + gridRect.origin.y.length,
          ]),
      [gridRect, numRows]
    );
  
    const drawContours = (ctx) => {
      contours.forEach((contour, i) => {
        const projection = d3.geoTransform({
          point: function (x, y) {
            this.stream.point(xScale(x), yScale(y));
          },
        });
        const pathGenerator = d3.geoPath().projection(projection);
        const pathData = pathGenerator(contour);
        const path2d = new Path2D(pathData);
        ctx.strokeStyle = autoContourValues
          ? colorScale(contour.value) 
          : contourColors[i];
        ctx.lineWidth = 1 / scale;
        ctx.stroke(path2d);
      });
    };
  
    // The sceneFunc for the Shape uses throttleRef to prevent continuous redraws.
    const sceneFunc = (ctx, shape) => {
        drawContours(ctx);
    };
  
    // Reset the throttle flag unconditionally whenever gridValues changes.
    useEffect(() => {
      throttleRef.current = false;
      if (shapeRef.current && shapeRef.current.getLayer()) {
        shapeRef.current.getLayer().batchDraw();
      }
    }, [gridValues]);
  
    return (
      <Layer>
        <Shape ref={shapeRef} sceneFunc={sceneFunc} />
      </Layer>
    );
};

export default ContourMapLayer;